import ErrorHandlerHelpers from '../../mixins/ErrorHandlerHelpers';
import SafetyObservationService from '../../services/SafetyObservationService';
import Idb from '../../services/IndexedDbService';

const SET_SAFETY_OBSERVATIONS = 'SET_SAFETY_OBSERVATIONS';
const ADD_SAFETY_OBSERVATION = 'ADD_SAFETY_OBSERVATION';
const REMOVE_SAFETY_OBSERVATION = 'REMOVE_SAFETY_OBSERVATION';
const CLEAR_SAFETY_OBSERVATIONS = 'CLEAR_SAFETY_OBSERVATIONS';

const state = {
  safetyObservations: []
};

const getters = {
  getSafetyObservations: () => state.safetyObservations,
  getSyncedAndUnsyncedSafetyObservationsForVoyage:
    (state, getters, rootState) => (voyageReportId) => {
      const unsyncedSafetyObservations = rootState.dataSyncStore.safetyObservationsToBeSynced;
      const unsyncedSafetyObservationsForVoyage = unsyncedSafetyObservations
        .filter((x) => x.voyageReportId === voyageReportId
          || x.voyageReportIdc === voyageReportId);

      // Filter outdated synced safetyObservations (safety observations that have been updated while offline)
      const syncedIdFilter = unsyncedSafetyObservations.map((x) => x.id);
      const filteredSyncedSafetyObservations = state.safetyObservations
        .filter((x) => !syncedIdFilter.includes(x.id));

      const result = []
        .concat(unsyncedSafetyObservationsForVoyage || [])
        .concat(filteredSyncedSafetyObservations);

      return result;
    },
  getPhotoFileUrl: (state, getters, rootState) => (fileName, safetyObservationId) => {
    const safetyObservation = state.safetyObservations.find(so => so.id === safetyObservationId);
    return SafetyObservationService.getPhotoFileUrl(fileName, safetyObservation);
  }
};

const mutations = {
  [SET_SAFETY_OBSERVATIONS](state, safetyObservations) {
    state.safetyObservations = safetyObservations;
  },
  [ADD_SAFETY_OBSERVATION](state, safetyObservation) {
    state.safetyObservations.push(safetyObservation);
  },
  [REMOVE_SAFETY_OBSERVATION](state, safetyObservationId) {
    state.safetyObservations = state.safetyObservations.filter((x) => x.id !== safetyObservationId);
  },
  [CLEAR_SAFETY_OBSERVATIONS](state) {
    state.safetyObservations = [];
  }
};

const actions = {
  loadSafetyObservationsForVoyage({ commit, dispatch }, voyageReportId) {
    return SafetyObservationService.getSafetyObservationsForVoyage(voyageReportId)
      .then((x) => x.data)
      .then((safetyObservations) => commit(SET_SAFETY_OBSERVATIONS, safetyObservations)) // Populate this store from server
      .then(dispatch('dataSyncStore/loadSafetyObservationsToBeSynced', null, { root: true })) // Populate sync store from unsynced
      .catch(ErrorHandlerHelpers.handleServiceError);
  },
  createSafetyObservation({ commit, rootState, dispatch }, newSafetyObservationModel) {
    const online = rootState.userStore.isClientConnectedToServer;
    if (online) {
      return SafetyObservationService.createSafetyObservation(newSafetyObservationModel)
        .then((x) => x.data)
        .then((resp) => {
          const createdSafetyObservation = resp.entity;
          commit(ADD_SAFETY_OBSERVATION, createdSafetyObservation);
          return resp;
        })
        .catch(ErrorHandlerHelpers.handleServiceError);
    }

    // TODO: Consider whether status is required and whether to set it here or rely on server?
    const newSafetyObservationIdbModel = { ...newSafetyObservationModel };

    return Idb.saveSafetyObservation(newSafetyObservationIdbModel)
      .then((createdSafetyObservationIdc) => Idb.getSafetyObservationByIdc(createdSafetyObservationIdc)
        .then((safetyObservation) => {
          commit('dataSyncStore/ADD_SAFETY_OBSERVATION_TO_BE_SYNCED', safetyObservation, { root: true });
          const result = { message: 'Safety Observation Created' };
          return result;
        })).catch(ErrorHandlerHelpers.handleServiceError);
  },
  updateSafetyObservation({ dispatch, rootState }, updateSafetyObservationModel) {
    const online = rootState.userStore.isClientConnectedToServer;
    if (online && updateSafetyObservationModel.id && !updateSafetyObservationModel.idc) {
      return SafetyObservationService.updateSafetyObservation(updateSafetyObservationModel)
        .then((x) => x.data)
        .then((resp) => {
          const voyageReportId = resp.entity.voyageReport.id;
          dispatch('loadSafetyObservationsForVoyage', voyageReportId);
          return resp;
        }).catch(ErrorHandlerHelpers.handleServiceError);
    }

    // TODO: Consider whether status is required and whether to set it here or rely on server?
    const updateSafetyObservationIdbModel = { ...updateSafetyObservationModel };

    return Idb.updateSafetyObservation(updateSafetyObservationIdbModel).then(() => {
      dispatch('loadSafetyObservationsForVoyage', updateSafetyObservationModel.voyageReportId);
      const result = { message: 'Safety Observation Updated' };
      return result;
    }).catch(ErrorHandlerHelpers.handleServiceError);
  },
  deleteSafetyObservation({ commit, rootState, dispatch }, safetyObservation) {
    const online = rootState.userStore.isClientConnectedToServer;
    const { id, idc } = safetyObservation;
    if (online && id && !idc) {
      return SafetyObservationService.deleteSafetyObservation(id)
        .then((x) => x.data)
        .then((result) => {
          if (result.success) commit(REMOVE_SAFETY_OBSERVATION, id);
          return result;
        })
        .catch(ErrorHandlerHelpers.handleServiceError);
    }

    return Idb.deleteSafetyObservation(safetyObservation).then(() => {
      dispatch('loadSafetyObservationsForVoyage', safetyObservation.voyageReportId);
      const result = { message: 'Safety Observation Deleted' };
      return result;
    }).catch(ErrorHandlerHelpers.handleServiceError);
  },
  clearSafetyObservations({ commit }) {
    commit(CLEAR_SAFETY_OBSERVATIONS);
  },
  uploadPhoto({ state, dispatch }, { file, safetyObservationId }) {
    const safetyObservation = state.safetyObservations.find(so => so.id === safetyObservationId);
    return SafetyObservationService.uploadPhoto(file, safetyObservation)
      .then(() => dispatch('loadSafetyObservationsForVoyage', safetyObservation.voyageReport.id))
      .catch(ErrorHandlerHelpers.handleServiceError);
  },
  deletePhoto({ state, dispatch }, { filename, safetyObservationId }) {
    const safetyObservation = state.safetyObservations.find(so => so.id === safetyObservationId);
    return SafetyObservationService.deletePhoto(filename, safetyObservation)
      .then(() => dispatch('loadSafetyObservationsForVoyage', safetyObservation.voyageReport.id))
      .catch(ErrorHandlerHelpers.handleServiceError);
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
